import { Checkbox, Divider, List, ListItemButton, ListItemIcon, ListItemText } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useEffect, useMemo, useState } from 'react';
import SearchInput from './SearchInput';

export interface SelectableListProps<T> {
    items: T[];
    selected: T[];
    getKey: (item: T) => string;
    getLabel: (item: T) => string;
    onChange: (newSelected: T[]) => void;
    renderIcon?: (item: T) => React.ReactNode;
}

export default function SelectableList<T>({ items, selected, getKey, getLabel, onChange, renderIcon }: SelectableListProps<T>): React.JSX.Element {
  const { t } = useTranslation(['settings', 'common']);
  const [query, setQuery] = useState('');
  const [initialOrder, setInitialOrder] = useState<T[]>([]);

  useEffect(() => {
    const selectedItems = items
      .filter((item) => selected.includes(item))
      .sort((a, b) => getLabel(a).localeCompare(getLabel(b)));

    const unselectedItems = items
      .filter((item) => !selected.includes(item))
      .sort((a, b) => getLabel(a).localeCompare(getLabel(b)));

    setInitialOrder([...selectedItems, ...unselectedItems]);
  }, [query]);

  const filtered = useMemo(() => {
    return initialOrder.filter((item) =>
      getLabel(item).toLowerCase().includes(query.toLowerCase()));
  }, [query, initialOrder]);

  const isAllSelected = filtered.every((item) => selected.includes(item));
  const isIndeterminate = selected.some((item) => filtered.includes(item)) && !isAllSelected;

  const toggleItem = (item: T): void => {
    onChange(
      selected.includes(item)
        ? selected.filter((selectedItem) => selectedItem !== item)
        : [...selected, item],
    );
  };

  const toggleSelectAll = (): void => {
    if (isAllSelected) {
      onChange(selected.filter((item) => !filtered.includes(item)));
    } else {
      onChange([...selected, ...filtered.filter((item) => !selected.includes(item))]);
    }
  };

  return (
    <>
      <SearchInput value={query} onChange={setQuery} />
      <List>
        <ListItemButton onClick={toggleSelectAll}>
          <ListItemText>{t('settings:select_all')}</ListItemText>
          <Checkbox checked={isAllSelected} indeterminate={isIndeterminate} />
        </ListItemButton>
        <Divider sx={{ my: 1.5 }} />
        {filtered.map((item) => (
          <ListItemButton key={getKey(item)} onClick={() => toggleItem(item)}>
            {renderIcon && <ListItemIcon>{renderIcon(item)}</ListItemIcon>}
            <ListItemText primary={getLabel(item)} />
            <Checkbox checked={selected.includes(item)} />
          </ListItemButton>
        ))}
      </List>
    </>
  );
}
